import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Building2 } from "lucide-react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { AdminHeader, ImageUpload } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = {
  name: "", description: "", website_url: "", open_account_url: "", logo_file_id: null,
  regulation: "", min_deposit: "", leverage: "", spread_info: "", features: [], active: true, order: 0,
  benefit: { type: "none", discount_percent: null, duration_days: null, description: "" },
};

export default function AdminBrokers() {
  useSEO("Admin · Brokers");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/brokers").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (b) => { setForm({ ...empty, ...b, benefit: { ...empty.benefit, ...(b.benefit || {}) } }); setEditId(b.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form, order: Number(form.order) || 0,
      benefit: {
        type: form.benefit.type,
        discount_percent: form.benefit.discount_percent ? Number(form.benefit.discount_percent) : null,
        duration_days: form.benefit.duration_days ? Number(form.benefit.duration_days) : null,
        description: form.benefit.description || "",
      },
    };
    try {
      if (editId) await api.put(`/admin/brokers/${editId}`, payload);
      else await api.post("/admin/brokers", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => { await api.delete(`/admin/brokers/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <AdminHeader title="Brokers" subtitle="Manage partner brokers & benefits"
        action={<Button onClick={openNew} className="gradient-brand text-brand-navy font-semibold" data-testid="new-broker-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Broker</Button>} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 ? <p className="text-slate-500 col-span-full">No brokers yet.</p>
          : rows.map((b) => (
            <div key={b.id} className="card-surface rounded-2xl p-5" data-testid={`admin-broker-${b.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-lg bg-brand-elevated flex items-center justify-center overflow-hidden">{b.logo_file_id ? <img src={fileUrl(b.logo_file_id)} alt={b.name} className="h-full w-full object-contain p-1" /> : <Building2 className="h-5 w-5 text-slate-500" />}</div>
                  <div><p className="font-display font-semibold text-white">{b.name}</p><p className="text-xs text-slate-500">{b.active ? "Active" : "Hidden"}</p></div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(b)} className="text-slate-500 hover:text-brand-cyan p-1" data-testid={`edit-broker-${b.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(b.id)} className="text-slate-500 hover:text-red-400 p-1" data-testid={`del-broker-${b.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {b.benefit?.type !== "none" && <p className="mt-3 text-xs text-brand-green capitalize">Benefit: {b.benefit.type.replace(/_/g, " ")}</p>}
            </div>
          ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-brand-surface border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editId ? "Edit" : "Add"} Broker</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <ImageUpload value={form.logo_file_id} onChange={(v) => setForm({ ...form, logo_file_id: v })} label="Logo" />
            <div><Label className="text-slate-300">Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="broker-name" className="mt-1.5 bg-white/5 border-white/10" /></div>
            <div><Label className="text-slate-300">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Open Account URL</Label><Input value={form.open_account_url} onChange={(e) => setForm({ ...form, open_account_url: e.target.value })} data-testid="broker-url" className="mt-1.5 bg-white/5 border-white/10" /></div>
              <div><Label className="text-slate-300">Regulation</Label><Input value={form.regulation} onChange={(e) => setForm({ ...form, regulation: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
              <div><Label className="text-slate-300">Min Deposit</Label><Input value={form.min_deposit} onChange={(e) => setForm({ ...form, min_deposit: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
              <div><Label className="text-slate-300">Leverage</Label><Input value={form.leverage} onChange={(e) => setForm({ ...form, leverage: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
            </div>
            <div className="rounded-xl border border-white/10 p-4 space-y-3">
              <p className="text-sm font-semibold text-brand-cyan">VIP Benefit (per-broker, configurable)</p>
              <Select value={form.benefit.type} onValueChange={(v) => setForm({ ...form, benefit: { ...form.benefit, type: v } })}>
                <SelectTrigger className="bg-white/5 border-white/10" data-testid="broker-benefit-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="discount">Discount %</SelectItem>
                  <SelectItem value="free_duration">Free VIP for X days</SelectItem>
                  <SelectItem value="lifetime">Lifetime VIP</SelectItem>
                </SelectContent>
              </Select>
              {form.benefit.type === "discount" && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-slate-300 text-xs">Discount %</Label><Input type="number" value={form.benefit.discount_percent || ""} onChange={(e) => setForm({ ...form, benefit: { ...form.benefit, discount_percent: e.target.value } })} data-testid="broker-discount" className="mt-1 bg-white/5 border-white/10" /></div>
                  <div><Label className="text-slate-300 text-xs">Duration days (optional)</Label><Input type="number" value={form.benefit.duration_days || ""} onChange={(e) => setForm({ ...form, benefit: { ...form.benefit, duration_days: e.target.value } })} data-testid="broker-discount-duration" className="mt-1 bg-white/5 border-white/10" /></div>
                </div>
              )}
              {form.benefit.type === "free_duration" && (
                <div><Label className="text-slate-300 text-xs">Free VIP duration (days)</Label><Input type="number" value={form.benefit.duration_days || ""} onChange={(e) => setForm({ ...form, benefit: { ...form.benefit, duration_days: e.target.value } })} data-testid="broker-duration" className="mt-1 bg-white/5 border-white/10" /></div>
              )}
              {form.benefit.type === "lifetime" && (
                <p className="text-xs text-brand-green">Approving a verification for this broker grants lifetime VIP.</p>
              )}
              {form.benefit.type !== "none" && (
                <Input value={form.benefit.description || ""} onChange={(e) => setForm({ ...form, benefit: { ...form.benefit, description: e.target.value } })} placeholder="Benefit label (optional)" className="bg-white/5 border-white/10" />
              )}
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="broker-active" /> Active</label>
              <div className="flex items-center gap-2"><Label className="text-slate-300 text-sm">Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="w-20 h-8 bg-white/5 border-white/10" /></div>
            </div>
            <DialogFooter><Button type="submit" className="gradient-brand text-brand-navy font-semibold" data-testid="broker-save">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
